import React, {
  useEffect,
  useState,
} from "react";

import {
  Link,
  useParams,
} from "react-router-dom";

import ProductOverview
  from "../components/products/ProductOverview";

import useProductOverview
  from "../hooks/useProductOverview";

import api
  from "../../../api/axios";

import "../styles/website.css";
import "../styles/product-overview.css";


const ProductOverviewPage = () => {

  const { slug } = useParams();


  const {
    overview,
    loading,
    error,
  } = useProductOverview(slug);


  const [
    categories,
    setCategories,
  ] = useState([]);


  const [
    categoryProducts,
    setCategoryProducts,
  ] = useState([]);


  const [
    categoryLoading,
    setCategoryLoading,
  ] = useState(false);


  const [
    categoryError,
    setCategoryError,
  ] = useState(null);


  /*
  |--------------------------------------------------------------------------
  | FETCH CATEGORIES
  |--------------------------------------------------------------------------
  */

  useEffect(() => {

    const fetchCategories = async () => {

      try {

        const response = await api.get(
          "/website-products/categories"
        );


        const data =
          response?.data?.data;


        setCategories(
          Array.isArray(data)
            ? data
            : []
        );

      } catch (err) {

        console.error(
          "Failed to fetch website categories:",
          err
        );

        setCategories([]);

      }

    };


    fetchCategories();

  }, []);


  /*
  |--------------------------------------------------------------------------
  | FETCH RELATED PRODUCTS
  |--------------------------------------------------------------------------
  */

  useEffect(() => {

    if (!overview?.category?.id) {

      setCategoryProducts([]);

      return;

    }


    const fetchCategoryProducts = async () => {

      try {

        setCategoryLoading(true);
        setCategoryError(null);


        const response = await api.get(
          "/website-products",
          {
            params: {
              categoryId:
                overview.category.id,

              page: 1,

              limit: 100,
            },
          }
        );


        const data =
          response?.data?.data;


        setCategoryProducts(
          Array.isArray(data?.products)
            ? data.products
            : []
        );

      } catch (err) {

        console.error(
          "Failed to fetch category products:",
          err
        );


        setCategoryError(
          err?.response?.data?.message ||
          "Unable to load related products."
        );


        setCategoryProducts([]);

      } finally {

        setCategoryLoading(false);

      }

    };


    fetchCategoryProducts();

  }, [
    overview?.category?.id,
  ]);


  /*
  |--------------------------------------------------------------------------
  | LOADING
  |--------------------------------------------------------------------------
  */

  if (loading) {

    return (
      <div className="website">

        <main className="product-overview-page">

          <div className="product-overview-loading">

            <div className="loading-spinner" />

            <p>
              Loading offering...
            </p>

          </div>

        </main>

      </div>
    );

  }


  /*
  |--------------------------------------------------------------------------
  | ERROR
  |--------------------------------------------------------------------------
  */

  if (error || !overview) {

    return (
      <div className="website">

        <main className="product-overview-page">

          <div className="product-overview-error">

            <span className="error-code">
              404
            </span>


            <h1>
              Offering Not Found
            </h1>


            <p>
              {error ||
                "The requested product could not be found."}
            </p>


            <Link
              to="/products"
              className="back-products-button"
            >
              ← Back to Products
            </Link>

          </div>

        </main>

      </div>
    );

  }


  return (
    <div className="website">

      <main className="product-overview-page">


        {/* =========================================================
            PRODUCT HERO
            ========================================================= */}

        <section className="product-overview-hero">

          <div className="product-overview-container">


            {/* Breadcrumb */}

            <div className="product-breadcrumb">

              <Link to="/products">
                Products
              </Link>

              <span>
                /
              </span>

              {overview.category?.name && (

                <>

                  <Link
                    to={`/products/${overview.category.slug}`}
                  >
                    {overview.category.name}
                  </Link>

                  <span>
                    /
                  </span>

                </>

              )}

              <span className="breadcrumb-current">
                {overview.name}
              </span>

            </div>


            {/* =====================================================
                MAIN PRODUCT HERO
                ===================================================== */}

            <div className="product-hero-card">


              {/* PRODUCT INFORMATION */}

              <div className="product-hero-content">

                {overview.category?.name && (

                  <span className="product-category">
                    {overview.category.name}
                  </span>

                )}


                <h1>
                  {overview.name}
                </h1>


                {overview.shortDescription && (

                  <p className="product-short-description">
                    {overview.shortDescription}
                  </p>

                )}


                <div className="product-hero-actions">

                  <a
                    href="#pricing"
                    className="product-primary-button"
                  >
                    View Plans
                    <span>
                      ↓
                    </span>
                  </a>


                  <Link
                    to="/contact"
                    className="product-secondary-button"
                  >
                    Contact Us
                  </Link>

                </div>


                <div className="product-hero-meta">

                  <div className="product-meta-item">

                    <span>
                      Category
                    </span>

                    <strong>
                      {overview.category?.name ||
                        "Business Solution"}
                    </strong>

                  </div>


                  <div className="product-meta-item">

                    <span>
                      Availability
                    </span>

                    <strong className="available-status">
                      <i />
                      Available
                    </strong>

                  </div>


                  <div className="product-meta-item">

                    <span>
                      Support
                    </span>

                    <strong>
                      Contact Our Team
                    </strong>

                  </div>

                </div>

              </div>


              {/* PRODUCT IMAGE */}

              <div className="product-hero-visual">

                {overview.image?.url ? (

                  <img
                    src={overview.image.url}
                    alt={
                      overview.image.altText ||
                      overview.name
                    }
                    className="product-hero-image"
                  />

                ) : overview.imageUrl ? (

                  <img
                    src={overview.imageUrl}
                    alt={overview.name}
                    className="product-hero-image"
                  />

                ) : (

                  <div className="product-hero-image-placeholder">

                    <span>
                      {overview.name
                        ?.charAt(0)
                        ?.toUpperCase()}
                    </span>

                  </div>

                )}

              </div>

            </div>

          </div>

        </section>


        {/* =========================================================
            ABOUT PRODUCT
            ========================================================= */}

        <section className="product-about-section">

          <div className="product-overview-container">

            <div className="product-about-grid">


              <div className="product-about-heading">

                <span className="section-eyebrow">
                  About The Product
                </span>

                <h2>
                  Built to simplify
                  <span>
                    your business.
                  </span>
                </h2>

              </div>


              <div className="product-about-content">

                {overview.description ? (

                  <p>
                    {overview.description}
                  </p>

                ) : overview.shortDescription ? (

                  <p>
                    {overview.shortDescription}
                  </p>

                ) : (

                  <p>
                    Discover a solution designed
                    to help your business operate
                    more efficiently.
                  </p>

                )}

              </div>

            </div>


            {/* PRODUCT DETAILS */}

            <div className="product-details-strip">

              <div>

                <span>
                  Category
                </span>

                <strong>
                  {overview.category?.name ||
                    "Business Solution"}
                </strong>

              </div>


              <div>

                <span>
                  Availability
                </span>

                <strong className="detail-available">
                  <i />
                  Available
                </strong>

              </div>


              <div>

                <span>
                  Support
                </span>

                <strong>
                  Contact Our Team
                </strong>

              </div>

            </div>

          </div>

        </section>


        {/* =========================================================
            PRICING
            ========================================================= */}

        <section
          id="pricing"
          className="pricing-section"
        >

          <div className="product-overview-container">

            <div className="pricing-header">

              <span className="section-eyebrow">
                Pricing
              </span>


              <h2>
                Choose Your Plan
              </h2>


              <p>
                Select the plan that best fits
                your requirements.
              </p>

            </div>


            {overview.plans?.length > 0 ? (

              <div className="pricing-grid">

                {overview.plans.map(
                  (plan, index) => (

                    <article
                      key={
                        plan.id ||
                        plan._id ||
                        index
                      }
                      className={
                        `pricing-card ${
                          index === 0
                            ? "pricing-card-primary"
                            : ""
                        }`
                      }
                    >

                      <div className="pricing-card-header">

                        <h3>
                          {plan.name}
                        </h3>


                        {plan.type && (

                          <span className="pricing-type">
                            {plan.type}
                          </span>

                        )}

                      </div>


                      {plan.description && (

                        <p className="pricing-description">
                          {plan.description}
                        </p>

                      )}


                      <div className="pricing-price">

                        <span className="price">
                          ₹
                          {Number(
                            plan.price || 0
                          ).toLocaleString(
                            "en-IN",
                            {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            }
                          )}
                        </span>

                      </div>


                      <Link
                        to="/contact"
                        className="pricing-button"
                      >
                        Get Started
                      </Link>

                    </article>

                  )
                )}

              </div>

            ) : (

              <div className="pricing-empty">

                <h3>
                  Pricing available on request
                </h3>

                <p>
                  Contact our team to discuss
                  the right plan for your business.
                </p>


                <Link
                  to="/contact"
                  className="pricing-button pricing-empty-button"
                >
                  Contact Us
                </Link>

              </div>

            )}

          </div>

        </section>


        {/* =========================================================
            CONTACT CTA
            ========================================================= */}

        <section className="product-query-section">

          <div>

            <span className="section-eyebrow">
              Get Started
            </span>


            <h2>
              Interested in{" "}
              {overview.name}?
            </h2>


            <p>
              Get in touch with our team to
              discuss your requirements.
            </p>

          </div>


          <Link
            to="/contact"
            className="query-button"
          >
            Contact Us
            <span>
              →
            </span>
          </Link>

        </section>


        {/* =========================================================
            RELATED PRODUCTS
            ========================================================= */}

        <section className="product-category-browser">

          <div className="product-category-browser-container">


            <div className="product-category-browser-header">

              <span className="section-eyebrow">
                Explore Our Products
              </span>


              <h2>
                Browse by Category
              </h2>


              <p>
                Explore other products without
                leaving this page.
              </p>

            </div>


            {/* CATEGORY NAVIGATION */}

            {categories.length > 0 && (

              <div className="product-category-list">

                <Link
                  to="/products"
                  className="product-category-filter"
                >
                  All Products
                </Link>


                {categories.map(
                  (category) => (

                    <Link
                      key={category.id}
                      to={`/products/${category.slug}`}
                      className={
                        `product-category-filter ${
                          overview.category?.id ===
                          category.id
                            ? "active"
                            : ""
                        }`
                      }
                    >

                      <span>
                        {category.name}
                      </span>


                      {typeof category.productCount ===
                        "number" && (

                        <span className="product-category-count">
                          {category.productCount}
                        </span>

                      )}

                    </Link>

                  )
                )}

              </div>

            )}


            {/* RELATED PRODUCTS */}

            <div className="related-products-header">

              <div>

                <span className="section-eyebrow">
                  {overview.category?.name ||
                    "More Products"}
                </span>


                <h3>
                  More Products in{" "}
                  {overview.category?.name}
                </h3>

              </div>

            </div>


            {categoryLoading && (

              <div className="related-products-loading">

                <div className="loading-spinner" />

                <p>
                  Loading products...
                </p>

              </div>

            )}


            {!categoryLoading &&
              categoryError && (

                <div className="related-products-error">

                  <p>
                    {categoryError}
                  </p>

                </div>

              )}


            {!categoryLoading &&
              !categoryError &&
              categoryProducts.length > 0 && (

                <div className="related-products-grid">

                  {categoryProducts.map(
                    (product) => (

                      <article
                        key={product.id}
                        className={
                          `related-product-card ${
                            product.slug === slug
                              ? "current-product"
                              : ""
                          }`
                        }
                      >

                        <div className="related-product-image">

                          {product.image?.url ? (

                            <img
                              src={product.image.url}
                              alt={
                                product.image.altText ||
                                product.name
                              }
                            />

                          ) : (

                            <div className="related-product-placeholder">

                              {product.name
                                ?.charAt(0)
                                ?.toUpperCase()}

                            </div>

                          )}

                        </div>


                        <div className="related-product-content">

                          <span className="related-product-category">
                            {product.category?.name ||
                              overview.category?.name}
                          </span>


                          <h4>
                            {product.name}
                          </h4>


                          {product.shortDescription && (

                            <p>
                              {product.shortDescription}
                            </p>

                          )}


                          {product.slug === slug ? (

                            <span className="related-product-current">
                              Currently Viewing
                            </span>

                          ) : (

                            <Link
                              to={`/products/${product.slug}`}
                              className="related-product-link"
                            >
                              View Product
                              <span>
                                →
                              </span>
                            </Link>

                          )}

                        </div>

                      </article>

                    )
                  )}

                </div>

              )}


            {!categoryLoading &&
              !categoryError &&
              categoryProducts.length === 0 && (

                <div className="related-products-empty">

                  <h3>
                    No other products available
                  </h3>


                  <p>
                    There are currently no other
                    products available in this category.
                  </p>

                </div>

              )}

          </div>

        </section>

      </main>

    </div>
  );

};


export default ProductOverviewPage;